import { useRouter } from 'next/router'
import React, { FunctionComponent, useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import DisclaimerText from 'components/DisclaimerText'
import Header from 'components/GlobalStyles/Header'
import RoundedButton from 'components/RoundedButton'
import Checkmark from 'components/SVG/Checkmark'
import { LeftChevron, RightChevron } from 'components/SVG/Chevron'
import DeleteX from 'components/SVG/DeleteX'
import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'

let imgSrc = `${process.env.CDN_BASE_URL}/q_auto/amazon/assets/c/1280/homelister-fsbo-yard-sign.jpg`
let tabletImgSrc = `${process.env.CDN_BASE_URL}/q_auto/amazon/assets/c/768/homelister-fsbo-yard-sign.jpg`
let imgSrcAlt = 'homelister-yard-sign'

const checklists = [
  'Total Control Over Your Sale',
  'Listing on MLS, Zillow, Trulia, Redfin, Realtor.com, and all other real estate search websites',
  'Professional For Sale Yard Sign, Photography, 3-D Video Walkthroughs',
  'Online Dashboard to Manage Sale',
  'Professional Help At Anytime',
]

const mlsChecklists = [
  'Listed on Zillow, Trulia, Redfin, Realtor.com, all other real estate search websites',
  'Millions of Impressions from Potential Buyers',
]

const chartData = [
  {
    id: 0,
    name: 'HOMELISTER',
    checklist: [true, true, true, true, true],
  },
  {
    id: 1,
    name: 'FOR SALE BY OWNER',
    checklist: [true, false, false, false, false],
  },
  {
    id: 2,
    name: 'OFFLINE TRADITIONAL AGENT',
    checklist: [false, true, true, false, true],
  },
  {
    id: 3,
    name: 'iBUYER',
    checklist: [false, false, false, false, true],
  },
]

const mlsChartData = [
  {
    id: 0,
    name: 'Get Listed in MLS via HomeLister',
    checklist: [true, true],
  },
  {
    id: 1,
    name: 'Not Listed in the MLS',
    checklist: [false, false],
  },
]

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 30px 0;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 40px 0;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 50px 50px;
  }
`

const Picture = styled.picture`
  img {
    width: 100%;
    height: auto;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 50px;
    max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  }
`

const DescriptionContainer = styled.div`
  padding: 20px;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 30px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 0;
  }
`

const Description = styled.p`
  font-size: 14px;
  font-weight: 300;
  line-height: 24px;
  margin: 0;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 20px;
    line-height: 35px;
  }
`

const KeywordsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-column-gap: 5px;
  text-align: center;

  @media (max-width: ${props => props.theme.metrics.phone}px) {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 10px;
    text-align: left;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;
    text-align: left;
  }
`

const Keyword = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: ${props => props.theme.colors.blue4};
  padding: 10px 0;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 20px;
  }
`

const GetListedContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
    font-size: 20px;
  }
`

interface ITextProps {
  hideOnMobile?: boolean
  hideOnTablet?: boolean
}
const Text = styled.div<ITextProps>`
  ${props => props.hideOnMobile && 'display: none;'}
  font-size: 18px;
  text-align: center;
  margin-bottom: 12px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: ${props => (props.hideOnTablet ? 'none' : 'block')};
    font-size: 24px;
    margin-top: 5px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin-top: 10px;
  }
`

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  img {
    object-fit: contain;
    height: 25px;
    margin: 10px 0;
  }

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
    max-width: ${props => props.theme.metrics.contentMaxWidth}px;
    width: 100%;
    height: 30px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    height: 40px;
  }
`

interface ICampaignType {
  mlsCampaign?: boolean
}

const ComparisonContainer = styled.div<ICampaignType>`
  display: grid;
  grid-template-columns: 45% 50%;
  position: relative;
  width: 100%;
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  background-color: ${props => props.theme.colors.grey2};
  padding: 30px 15px;
  padding-bottom: 70px;

  ${props => props.mlsCampaign && 'padding-bottom: 30px;'}

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 30px;
    padding-bottom: 80px;
    ${props => props.mlsCampaign && 'padding-bottom: 30px;'}
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    grid-template-columns: 40% 60%;
    padding: 30px 50px;
  }
`

const ComparisonFactorsGrid = styled.div<ICampaignType>`
  display: grid;
  grid-template-rows: 1fr 2fr 1.75fr 1fr 1fr;
  grid-row-gap: 10px;
  align-items: center;
  height: 250px;
  ${props =>
    props.mlsCampaign
    && `
    grid-template-rows: 1fr 1fr;
    height: 150px;
  `}

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 16px;
    grid-template-rows: 1fr 1.75fr 1.5fr 1fr 1fr;
    ${props => props.mlsCampaign && 'grid-template-rows: 1fr 1fr;'}
  }

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    grid-template-rows: ${props => (props.mlsCampaign ? '1fr 1fr' : '1fr 1.5fr 1.5fr 1fr 1fr')};
    grid-row-gap: 20px;
  }
`

const ChartBody = styled.div<ICampaignType>`
  display: grid;
  grid-template-rows: ${props => (props.mlsCampaign ? '1fr 1fr' : '1fr 2fr 1.75fr 1fr 1fr')};
  grid-row-gap: 10px;
  justify-items: center;
  align-items: center;
  height: 100%;

  @media (min-width: ${props => props.theme.metrics.phone}px) {
    grid-template-rows: ${props => (props.mlsCampaign ? '1fr 1fr' : '1fr 1.75fr 1.5fr 1fr 1fr')};
  }

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    grid-template-rows: ${props => (props.mlsCampaign ? '1fr 1fr' : '1fr 1.5fr 1.5fr 1fr 1fr')};
    grid-row-gap: 20px;
  }
`

const ComparisonFactor = styled.div`
  font-size: 12px;
  font-weight: 300;

  @media (min-width: ${props => props.theme.metrics.phone}px) {
    font-size: 14px;
  }

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 16px;
  }
`

const ChartGridContainer = styled.div<ICampaignType>`
  display: grid;
  grid-template-columns: minmax(50px, 100px) minmax(50px, 100px);
  grid-template-rows: 1fr 20px;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  position: absolute;
  right: ${props => props.theme.metrics.gutterPhone}px;
  top: -80px;
  width: 45%;

  ${props =>
    props.mlsCampaign
    && `
    grid-template-columns: 40% 40%;
    grid-template-rows: 1fr;
    right: 0;
  `}

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    grid-template-columns: minmax(100px, 135px) minmax(100px, 135px);
    grid-column-gap: 15px;
    grid-row-gap: 20px;
    width: 40%;

    ${props =>
    props.mlsCampaign
      && `
      grid-template-columns: 40% 40%;
    `}
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    grid-template-columns: minmax(100px, 135px) minmax(100px, 135px) minmax(100px, 135px) minmax(
        100px,
        135px
      );
    width: 55%;

    ${props =>
    props.mlsCampaign
      && `
      grid-template-columns: 45% 45%;
      width: 50%;
    `}
  }
`

interface IChartProps {
  accent?: boolean
}
const ChartBox = styled.div<IChartProps & ICampaignType>`
  color: white;
  text-align: center;
  font-size: 9px;
  font-weight: ${props => (props.accent ? '800' : '500')};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: ${props => (props.accent ? '14px' : '12px')};

    ${props => props.mlsCampaign && 'font-size: 14px;'}
  }
`

const ChartHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70px;
  background-color: ${props => props.theme.colors.blue4};
  padding: 8px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    white-space: pre-line;
  }
`

const ChartBodyWrapper = styled.div<IChartProps & ICampaignType>`
  background-color: ${props =>
    props.accent ? props.theme.colors.blue9 : props.theme.colors.blue10};
  height: ${props => (props.mlsCampaign ? '200px' : '300px')};
  padding-top: 40px;
  padding-bottom: 15px;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding-bottom: 10px;
  }
`

const ChartSliderContainer = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  width: 100%;
  bottom: 0;
  grid-column: 1 / span 2;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: none;
  }
`

interface IChartControlProps {
  active?: boolean
}
const ChartSliderControl = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100px;
`

interface IDisabledProps {
  disabled?: boolean
}
const ControlButton = styled.button<IDisabledProps>`
  display: flex;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: none;
  svg path {
    fill: ${props => (props.disabled ? props.theme.colors.grey8 : props.theme.colors.blue4)};
  }
`

const Dot = styled.div<IChartControlProps>`
  width: 10px;
  height: 10px;
  border-radius: 100%;
  background-color: ${props =>
    props.active ? props.theme.colors.blue4 : props.theme.colors.grey8};
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 275px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    width: 375px;
  }
`
interface IProps {
  title: string
  content: string
  campaignType: string
  freeToList?: boolean
}

const ComparisonChart: FunctionComponent<IProps> = ({
  title,
  content,
  campaignType,
  freeToList,
}) => {
  const theme = useContext(ThemeContext)
  const router = useRouter()
  const Xmark = DeleteX
  const { width } = useWindowSize()

  const [currentChartIndex, setCurrentChartIndex] = useState(1)

  const campaignRoute = router.asPath.split('c/').pop() || ''

  // set state specific changes
  if (['utah'].includes(campaignRoute)) {
    imgSrc = `${process.env.CDN_BASE_URL}/q_auto/amazon/assets/c/1280/grey-home.jpg`
    tabletImgSrc = `${process.env.CDN_BASE_URL}/q_auto/amazon/assets/c/768/grey-home.jpg`
    imgSrcAlt = 'A grey home'

    // Update chart first column title
    chartData[0].name = 'HOMELISTER'
  }

  const displayComparisonChart = () => {
    let currentChecklists = checklists
    let currentCharts
      = width && width < theme.metrics.desktop
        ? [chartData[0], chartData[currentChartIndex]]
        : chartData

    // MLS campaign has a different chart data
    const mlsCampaign: boolean = campaignType === 'mls'

    if (mlsCampaign) {
      currentChecklists = mlsChecklists
      currentCharts = mlsChartData
    }

    const handleControl = (event: React.MouseEvent) => {
      let index = currentChartIndex
      const { name } = event.currentTarget as HTMLButtonElement

      if (name === 'next') {
        index += 1
        if (index >= chartData.length - 1) index = chartData.length - 1
      } else {
        index -= 1
        if (index < 1) index = 1
      }

      setCurrentChartIndex(index)
    }

    return (
      <ComparisonContainer mlsCampaign={mlsCampaign}>
        <ComparisonFactorsGrid mlsCampaign={mlsCampaign}>
          {currentChecklists.map(list => (
            <ComparisonFactor key={list}>{list}</ComparisonFactor>
          ))}
        </ComparisonFactorsGrid>
        <ChartGridContainer mlsCampaign={mlsCampaign}>
          {currentCharts.map(chart => (
            <ChartBox
              accent={chart.name === 'HOMELISTER'}
              mlsCampaign={mlsCampaign}
              key={chart.name}
            >
              <ChartHeader>{chart.name}</ChartHeader>
              <ChartBodyWrapper
                accent={chart.name === 'HOMELISTER'}
                mlsCampaign={mlsCampaign}
              >
                <ChartBody mlsCampaign={mlsCampaign}>
                  {chart.checklist.map((check, checkIndex) =>
                    check
                      ? (
                        <Checkmark
                          fill={theme.colors.white}
                          width={15}
                          height={12}
                          key={`${chart.name}-${checklists[checkIndex]}`}
                        />
                      )
                      : (
                        <Xmark
                          fill={theme.colors.blue4}
                          width={12}
                          height={12}
                          key={`${chart.name}-${checklists[checkIndex]}`}
                        />
                      ),
                  )}
                </ChartBody>
              </ChartBodyWrapper>
            </ChartBox>
          ))}
          {!mlsCampaign && (
            <ChartSliderContainer>
              <ChartSliderControl>
                <ControlButton
                  name="prev"
                  disabled={currentChartIndex <= 1}
                  onClick={handleControl}
                >
                  <LeftChevron
                    fill={theme.colors.blue4}
                    width={15}
                    height={12}
                  />
                </ControlButton>
                {chartData.map(
                  (chart, chartIndex) =>
                    chartIndex > 0 && (
                      <Dot
                        active={currentChartIndex === chartIndex}
                        key={chart.id}
                      />
                    ),
                )}
                <ControlButton
                  name="next"
                  onClick={handleControl}
                  disabled={currentChartIndex >= chartData.length - 1}
                >
                  <RightChevron
                    fill={theme.colors.blue4}
                    width={15}
                    height={12}
                  />
                </ControlButton>
              </ChartSliderControl>
            </ChartSliderContainer>
          )}
        </ChartGridContainer>
      </ComparisonContainer>
    )
  }

  return (
    <Container>
      <Header
        text={title}
        fontSize={24}
        fontTabletSize={32}
        fontDesktopSize={42}
        mobilePadding="0 15px"
        headerThree
      />
      <Spacer
        size={50}
        tabletSize={40}
        responsiveSize={30}
      />
      <ContentContainer>
        <Picture>
          <source
            srcSet={imgSrc}
            media={`(min-width: ${theme.metrics.desktop}px)`}
          />
          <source
            srcSet={tabletImgSrc}
            media={`(min-width: ${theme.metrics.tablet}px)`}
          />
          <img
            src={imgSrc}
            alt={imgSrcAlt}
          />
        </Picture>
        <DescriptionContainer>
          <Description>{content}</Description>
          <Spacer size={20} />
          <KeywordsContainer>
            <Keyword>{freeToList ? 'Free to List*, Pay at Close' : 'Low Flat Fees'}</Keyword>
            <Keyword>MLS and All Sites</Keyword>
            <Keyword>Easy Process</Keyword>
            <Keyword>Rated 4.8/5 on Zillow</Keyword>
          </KeywordsContainer>
          {freeToList && <DisclaimerText>*Availability varies by location</DisclaimerText>}
        </DescriptionContainer>
      </ContentContainer>
      <Spacer
        size={120}
        tabletSize={100}
        responsiveSize={100}
      />
      {displayComparisonChart()}
      <Spacer
        size={50}
        tabletSize={40}
        responsiveSize={30}
      />
      <GetListedContainer>
        <Text hideOnTablet>Get listed On</Text>
        <LogoContainer>
          <Text hideOnMobile>Get listed On</Text>
          <img
            src={`${process.env.CDN_BASE_URL}/q_auto,w_200/amazon/assets/zillow.png`}
            alt="zillow"
          />
          <img
            src={`${process.env.CDN_BASE_URL}/q_auto,w_200/amazon/assets/trulia.png`}
            alt="trulia"
          />
          <img
            src={`${process.env.CDN_BASE_URL}/q_auto,w_200/amazon/assets/redfin.png`}
            alt="redfin"
          />
          <img
            src={`${process.env.CDN_BASE_URL}/q_auto,w_200/amazon/assets/Realtor.com_logo.png`}
            alt="realtor"
          />
        </LogoContainer>
      </GetListedContainer>
      <Spacer
        size={50}
        tabletSize={40}
        responsiveSize={30}
      />
      <ButtonContainer>
        <RoundedButton
          text="See how it works"
          href="/how-it-works"
          color={theme.colors.white}
          background={theme.colors.blue4}
          hoverBackground={theme.colors.blue9}
          fontSize={16}
          radius={100}
          height={50}
          fullWidth
        />
      </ButtonContainer>
    </Container>
  )
}

export default ComparisonChart
